import { Link } from 'react-router-dom';
import type { Companion } from '@/lib/data/companions';
import { unitLabel } from '@/lib/data/services';
import { Avatar, Badge, Coin, PresenceDot, Rating, VerifiedMark } from '@/components/ui';

export default function CompanionCard({
  companion: c,
  index = 0,
}: {
  companion: Companion;
  index?: number;
}) {
  const available = c.isOnline && !c.isBusy;

  return (
    <Link
      to={`/companions/${c.id}`}
      className="group surface animate-rise relative flex flex-col rounded-2xl p-5 transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:border-[var(--marigold)] hover:shadow-[var(--shadow-lg)]"
      style={{ animationDelay: `${index * 55}ms` }}
    >
      {/* Warm wash that fades in behind the card contents on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background: 'linear-gradient(to bottom, var(--marigold-soft), transparent 60%)',
        }}
      />

      <div className="relative flex items-start gap-3.5">
        <Avatar
          initials={c.avatarInitials}
          hue={c.avatarHue}
          size={56}
          active={available}
          photo={c.avatarUrl ?? `companion-${c.id}.jpg`}
          photoPosition={c.photoPosition}
          alt={c.name}
          className="transition-transform duration-200 group-hover:scale-105"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-display text-lg font-semibold">
              {c.name}
            </h3>
            {c.verified && <VerifiedMark />}
          </div>

          <p className="mt-0.5 line-clamp-1 text-sm text-[var(--ink-2)]">{c.headline}</p>

          <div className="mt-2 flex items-center gap-3">
            <PresenceDot isOnline={c.isOnline} isBusy={c.isBusy} />
            <Rating value={c.rating} count={c.ratingCount} />
          </div>
        </div>
      </div>

      {/* What they actually offer, from companion_services. This used to show
          the free-text `specialties` array, which nothing writes to since the
          pivot — so every card carried an empty row. */}
      <div className="relative mt-4 flex flex-wrap gap-1.5">
        {c.services.slice(0, 3).map((s) => (
          <Badge key={s.categorySlug}>{s.categoryName}</Badge>
        ))}
        {c.services.length > 3 && <Badge>+{c.services.length - 3} more</Badge>}
      </div>

      <p className="relative mt-3 text-xs text-[var(--muted)]">
        {c.languages.join(' · ')} — {c.city}
      </p>

      <div className="relative mt-4 flex items-center justify-between gap-3 border-t border-[var(--line-soft)] pt-3.5">
        {/* The cheapest way to book them. No rates at all means they have not
            finished setting up — showing "0 coins" would read as free. */}
        {c.cheapest ? (
          <span className="flex items-baseline gap-1.5 text-sm">
            <span className="text-xs text-[var(--muted)]">from</span>
            <Coin className="self-center text-[var(--marigold)]" />
            <span className="font-semibold tabular-nums">{c.cheapest.priceCoins}</span>
            <span className="text-xs text-[var(--muted)]">{unitLabel(c.cheapest.unit)}</span>
          </span>
        ) : (
          <span className="text-xs text-[var(--muted)]">Setting up their profile</span>
        )}

        <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--marigold)]">
          {available ? 'Talk now' : 'View'}
          <svg
            viewBox="0 0 16 16"
            className="size-3.5 transition-transform duration-200 group-hover:translate-x-1"
            aria-hidden
          >
            <path
              d="M3 8h9M8.5 4.5L12 8l-3.5 3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </Link>
  );
}
